<script setup lang="ts">
// Baked in at build time (@rollup/plugin-yaml). '/' is SSR at runtime for
// custom domain support, where queryCollection has no content database on
// serverless - a static import cannot fail there.
import page from '~~/content/0.index.yml'

const title = page?.seo?.title || page?.title
const description = page?.seo?.description || page?.description

useSeoMeta({
  titleTemplate: '',
  title,
  ogTitle: title,
  description,
  ogDescription: description
})

defineOgImageComponent('Saas')

// FAQPage from the landing FAQ ("What is mychar.gg?" first), so search and
// answer engines can lift the definitions directly. The showreel is a
// VideoObject (the dark film's 1080p MP4 is the canonical file; both posters
// are thumbnails), mirrored by the '/' video sitemap entry in nuxt.config.ts.
type LandingSection = { id: string, headline?: string, title: string, description: string, orientation?: 'horizontal' | 'vertical', reverse?: boolean, features?: { title: string, description: string, icon: string }[] }
const sectionById = (id: string) => (page?.sections as LandingSection[] | undefined)?.find(s => s.id === id)
const designSection = sectionById('design')
const analyticsSection = sectionById('analytics')

const supabaseUrl = String(useRuntimeConfig().public.supabase?.url ?? '')
const showreelFile = (file: string) => showreelUrl(supabaseUrl, file)

useSchemaOrg([
  defineWebPage({ '@type': ['WebPage', 'FAQPage'] }),
  ...(page?.faq?.items ?? []).map((item: { label: string, content: string }) => defineQuestion({ name: item.label, acceptedAnswer: item.content })),
  ...(supabaseUrl
    ? [defineVideo({
        name: SHOWREEL.name,
        description: SHOWREEL.description,
        thumbnailUrl: [showreelFile(SHOWREEL.files.dark.poster), showreelFile(SHOWREEL.files.light.poster)],
        // Explicit, or the video inherits the page's OG image as image/thumbnail
        image: showreelFile(SHOWREEL.files.dark.poster),
        uploadDate: SHOWREEL.uploadDate,
        duration: `PT${SHOWREEL.durationSeconds}S`,
        url: showreelFile(SHOWREEL.files.dark.mp4),
        contentUrl: showreelFile(SHOWREEL.files.dark.mp4),
        encodingFormat: 'video/mp4',
        width: SHOWREEL.width,
        height: SHOWREEL.height,
        inLanguage: 'en',
        isFamilyFriendly: true
      })]
    : [])
])

// Scroll reveal: hidden state is only applied once JS runs, so crawlers and
// no-JS visitors always see the content. Sections already in view stay put.
onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  const sections = Array.from(document.querySelectorAll<HTMLElement>('.landing-reveal'))
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('landing-reveal-visible')
          observer.unobserve(entry.target)
        }
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  )

  for (const el of sections) {
    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight * 0.9) {
      // Already on screen at load: no animation, no flash
      el.classList.add('landing-reveal-visible')
      continue
    }
    el.classList.add('landing-reveal-init')
    observer.observe(el)
  }

  onUnmounted(() => observer.disconnect())
})
</script>

<template>
  <div v-if="page">
    <!-- Hero: one message, one action, then the product itself (the showreel) -->
    <UPageHero
      :description="page.description"
      :ui="{
        container: 'pb-10 sm:pb-14 lg:pb-16 gap-10 sm:gap-12',
        title: 'mx-auto max-w-3xl',
        description: 'mx-auto max-w-2xl',
        links: 'w-full justify-center'
      }"
    >
      <template #title>
        <MDC
          :value="page.title"
          unwrap="p"
        />
      </template>

      <template #links>
        <div class="flex w-full flex-col items-center gap-3">
          <HeroClaimUsername
            variant="compact"
            location="hero"
          />
          <ul
            v-if="page.hero?.reassurance"
            class="flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
          >
            <li
              v-for="item in page.hero.reassurance"
              :key="item"
              class="flex items-center gap-1.5 text-sm text-muted"
            >
              <UIcon
                name="i-lucide-check"
                class="size-4 text-primary-400"
              />
              {{ item }}
            </li>
          </ul>
        </div>
      </template>

      <!-- Video files and SEO metadata: shared/utils/showreel.ts -->
      <HomeShowreel />
    </UPageHero>

    <!-- Platforms strip -->
    <section
      v-if="page.logos"
      class="landing-reveal border-y border-default bg-muted/40"
    >
      <UContainer class="flex flex-col items-center gap-6 py-10">
        <p class="text-sm text-muted">
          {{ page.logos.title }}
        </p>
        <div class="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 text-dimmed">
          <UIcon
            v-for="icon in page.logos.icons"
            :key="icon"
            :name="icon"
            class="size-6 shrink-0"
          />
          <span class="text-sm text-dimmed">+ any custom link</span>
        </div>
      </UContainer>
    </section>

    <!-- Editor: the differentiator, with the interactive demo -->
    <UPageSection
      v-if="designSection"
      :headline="designSection.headline"
      :title="designSection.title"
      :description="designSection.description"
      :orientation="designSection.orientation"
      :reverse="designSection.reverse"
      :features="designSection.features"
      class="landing-reveal"
    >
      <ShowcaseDesign />
    </UPageSection>

    <!-- Claim again right after the demo, where intent peaks -->
    <HomeInlineCta
      v-if="page.inlineCta"
      :title="page.inlineCta.title"
      :description="page.inlineCta.description"
      class="landing-reveal"
    />

    <!-- Everything else a page can hold, as one bento grid -->
    <UPageSection
      v-if="page.bento"
      :headline="page.bento.headline"
      :title="page.bento.title"
      :description="page.bento.description"
      class="landing-reveal"
    >
      <HomeBento :tiles="page.bento.tiles" />
    </UPageSection>

    <!-- Analytics -->
    <UPageSection
      v-if="analyticsSection"
      :headline="analyticsSection.headline"
      :title="analyticsSection.title"
      :description="analyticsSection.description"
      :orientation="analyticsSection.orientation"
      :reverse="analyticsSection.reverse"
      :features="analyticsSection.features"
      class="landing-reveal"
    >
      <ShowcaseAnalytics />
    </UPageSection>

    <!-- Pricing teaser (prices and payment mode from the pricing page sources) -->
    <HomePricingTeaser class="landing-reveal" />

    <!-- Leaderboard teaser -->
    <UPageSection
      headline="Leaderboard"
      title="Most viewed this week"
      description="Real pages from the community, ranked by views. Yours could be here."
      class="landing-reveal"
    >
      <LeaderboardTeaser />
    </UPageSection>

    <!-- FAQ -->
    <UPageSection
      v-if="page.faq"
      :title="page.faq.title"
      :description="page.faq.description"
      class="landing-reveal"
    >
      <UAccordion
        :items="page.faq.items"
        :unmount-on-hide="false"
        type="multiple"
        class="mx-auto flex max-w-3xl flex-col gap-3"
        :ui="{
          item: 'rounded-xl border border-default bg-elevated/40 px-4 border-b-0 last:border-b-0 shadow-(--surface-shadow)',
          trigger: 'text-base text-highlighted',
          body: 'text-base text-muted'
        }"
      />
    </UPageSection>

    <!-- Bottom CTA -->
    <CtaPanel
      :title="page.cta.title"
      :description="page.cta.description"
      class="landing-reveal"
    />

    <!-- Mobile: a slim claim bar while no claim field is on screen -->
    <HomeStickyCta />

    <!-- Site-wide announcement popup (client-only, fetched at runtime) -->
    <AnnouncementPopup />
  </div>
</template>

<style>
.hero-underline {
  text-decoration-line: underline;
  text-decoration-color: color-mix(in srgb, var(--color-primary-400) 75%, transparent);
  text-decoration-thickness: 4px;
  text-underline-offset: 7px;
}

.landing-reveal-init {
  opacity: 0;
  transform: translateY(18px);
}

.landing-reveal-visible {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}
</style>
